<template>
	<section class="client-account-returns-scatterplot grid position-relative p-150-md">
		<div v-if="!appStore.isDesktop">
			<h3 class="heading-5">{{ heading }}</h3>
			<p class="mt-50 mb-150 body-sm">{{ description }}</p>
		</div>
		<header class="width-100">
			<h3 v-if="appStore.isDesktop" class="heading-6">{{ heading }}</h3>
			<div class="display-flex justify-content-space-between gap-150 body-sm">
				<p v-if="appStore.isDesktop">{{ description }}</p>
				<p v-else class="font-weight-bold line-height-100"
					>Cumulative <br />
					net returns
				</p>
				<div class="display-flex align-items-center gap-50">
					<BaseSvgIcon name="standard-deviation" height="16px" /> Average +/- 1 standard deviation
				</div>
			</div>
		</header>
		<div class="col-12 pt-300-md pt-100-smo">
			<figure ref="chartFigureRef" class="position-relative m-0">
				<ChartPlaceholder v-if="showPlaceholder" :loading="showPlaceholder" style="height: 450px" />
				<highcharts v-else :options="chartOptions" />
			</figure>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import Highcharts, { Options } from 'highcharts';
import BaseChart from './BaseChartOptions';
import ChartPlaceholder from './chart-placeholder.vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import deepMerge from 'deepmerge';
import { getAccountReturnsByTimeOnPlatform } from '@api/historical-performance';
import { Chart as highcharts } from 'highcharts-vue';
import highchartsMore from 'highcharts/highcharts-more';
import { isElementFullyVisible } from '@utils/client';
import { percentage } from '@filters/shared-filters';
import spacetime from 'spacetime';
import { useAppStore } from '@stores/app';
import { useIdleCallback } from '@utils/composables/use-idle-callback';

const appStore = useAppStore();

const axisTitleStyle = {
	fontSize: '16px',
	fontWeight: '600',
	color: '#151513'
};

const heading = 'Advisory client returns by years on platform';

const averageReturnsByYear = ref<number[][]>([]);
const chartFigureRef = ref<HTMLElement | null>(null);
const isChartInView = ref(false);
const stdReturnsByYear = ref<Array<{ x: number; low: number; high: number }>>([]);
const loading = ref(true);

const chartOptions = computed((): Options => {
	let options: Options = {
		accessibility: {
			description:
				'This chart illustrates the average advisory client returns by the number of years investors have been on the Fundrise platform. The x-axis represents the number of years investors have been on the platform, starting from 0 years up to the maximum number of years available. The y-axis indicates the average advisory client returns as a percentage. For each year on the platform, there is a data point representing the average return. In addition, there are error bars extending one standard deviation above and below the average return. This helps to show the variability or spread of returns around the average.'
		},
		series: [
			{
				type: 'areasplinerange',
				fillColor: 'rgba(204, 214, 224, 0.30)',
				data: isChartInView.value ? stdReturnsByYear.value : [],
				lineWidth: 0,
				marker: {
					enabled: false,
					states: {
						hover: {
							enabled: false
						}
					}
				},
				animation: {
					easing: 'easeInOutSine',
					duration: 1000
				}
			},
			{
				type: 'errorbar',
				color: '#91B1BE',
				data: isChartInView.value ? stdReturnsByYear.value : [],
				lineWidth: 3,
				pointRange: 1,
				whiskerLength: 26,
				animation: {
					duration: 1000
				}
			} as Highcharts.SeriesErrorbarOptions,
			{
				type: 'line',
				color: '#517787',
				data: isChartInView.value ? averageReturnsByYear.value : [],
				lineWidth: appStore.isDesktop ? 5 : 3,
				marker: {
					enabled: true,
					radius: appStore.isDesktop ? 6 : 4,
					lineColor: 'white',
					lineWidth: 2
				},
				animation: {
					easing: 'easeInOutSine',
					duration: 1000
				}
			}
		],
		xAxis: {
			labels: {
				style: {
					fontSize: '14px',
					fontWeight: '600',
					color: '#373633'
				}
			},
			min: 0,
			minPadding: 0,
			minorTicks: false,
			showFirstLabel: false,
			tickColor: 'rgba(0,0,0,0)',
			tickInterval: 1,
			title: {
				text: 'Years elapsed since initial investment',
				style: axisTitleStyle
			},
			crosshair: {
				color: '#6B6A65',
				width: appStore.isDesktop ? 0 : 1
			}
		},
		yAxis: {
			gridLineColor: '#F2F1ED',
			gridLineDashStyle: 'LongDash',
			gridLineWidth: 1.25,
			labels: {
				formatter(): string {
					return percentage(this.value, true, 0);
				},
				style: {
					fontSize: '16px',
					fontWeight: '600',
					color: '#373633'
				}
			},
			lineWidth: 0,
			tickInterval: 0.25,
			title: {
				text: appStore.isDesktop ? 'Cumulative net returns' : '',
				style: axisTitleStyle
			}
		},
		plotOptions: {
			series: {
				connectNulls: true,
				marker: {
					states: {
						hover: {
							enabled: false
						}
					}
				},
				states: {
					hover: { enabled: false },
					inactive: {
						opacity: 1
					}
				}
			},
			areasplinerange: {
				dataLabels: {
					backgroundColor: 'white',
					borderColor: '#6B6A65',
					borderRadius: 16,
					borderWidth: 1,
					enabled: appStore.isDesktop,
					filter: {
						property: 'below',
						operator: '==',
						value: 0
					},
					formatter(): string | undefined {
						if (this.x === 0) {
							return;
						}
						const unitOfTime = !!this.x && (this.x as number) > 1 ? 'years' : 'year';
						const average = averageReturnsByYear.value[this.point.index][1];
						const percentReturn = percentage(average, true);
						return `<p class="body-xs text-color-content-secondary text-align-center">
									After ${this.x} ${unitOfTime}
									<span class="display-block font-weight-bold body-md">
										${percentReturn}
									</span>
								</p>`;
					},
					padding: 6,
					shape: 'callout',
					useHTML: true,
					yHigh: -16
				},
				states: {
					hover: { enabled: false },
					inactive: {
						opacity: 1
					}
				}
			}
		},
		tooltip: {
			enabled: !appStore.isDesktop,
			borderWidth: 0,
			shape: 'square',
			shadow: false,
			useHTML: true,
			formatter(): string {
				const unitOfTime = !!this.x && this.x > 1 ? 'years' : 'year';
				const average = averageReturnsByYear.value[this.point.index][1];
				const percentReturn = percentage(average, true);

				return `<div class="text-align-center boody-xs"><strong>${percentReturn}</strong> after ${this.x} ${unitOfTime}</div>`;
			},
			style: {
				color: ''
			}
		},
		responsive: {
			rules: [
				{
					condition: {
						minWidth: 0
					},
					chartOptions: {
						tooltip: {
							positioner(labelWidth, labelHeight): Highcharts.PositionObject {
								return {
									x: this.chart.plotLeft + (this.chart.plotWidth - labelWidth) / 2,
									y: 0 - labelHeight / 8
								};
							}
						}
					}
				}
			]
		}
	};

	if (appStore.isMobile) {
		options = {
			...options,
			chart: {
				height: '100%'
			}
		};
	}

	return deepMerge(new BaseChart().getOptions(), options);
});

const description = computed(
	(): string => `Average returns for each investor based on how many years they've been invested in Fundrise between
					2015-${thisYear.value}`
);

const showPlaceholder = computed((): boolean => loading.value || !isChartInView.value);

const thisYear = computed((): string => spacetime().format(CustomSpacetimeFormat.YEAR));

function triggerChartAnimation(): void {
	if (chartFigureRef.value && isElementFullyVisible(chartFigureRef.value)) {
		isChartInView.value = true;
		document.removeEventListener('scroll', triggerChartAnimation);
	}
}

onMounted(() => {
	document.addEventListener('scroll', triggerChartAnimation);
});

onUnmounted(() => {
	document.removeEventListener('scroll', triggerChartAnimation);
});

useIdleCallback(async () => {
	try {
		const apiResponse = await getAccountReturnsByTimeOnPlatform();

		averageReturnsByYear.value = apiResponse.map((stat) => [Number(stat.yearsOnPlatform), Number(stat.mean)]);
		averageReturnsByYear.value.unshift([0, 0]);

		stdReturnsByYear.value = apiResponse.map((stat) => ({
			x: Number(stat.yearsOnPlatform),
			low: Number(stat.mean) - Number(stat.standardDeviation),
			high: Number(stat.mean) + Number(stat.standardDeviation)
		}));
		stdReturnsByYear.value.unshift({ x: 0, low: 0, high: 0 });

		loading.value = false;
	} catch {}
});

async function setup(): Promise<void> {
	try {
		highchartsMore(Highcharts);
	} catch {}
}

setup();
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;
@use '../../styles/utilities/respond-to.scss' as *;

:deep(.highcharts-tooltip > span) {
	border: 1px solid gray(40);
	border-radius: 12px;
	padding: 3px 8px;
}

.client-account-returns-scatterplot {
	@include respond-to(md) {
		border: 1px solid token('bg-tertiary');
		border-radius: 0.5rem;
	}
}
</style>
